// eslint-disable-next-line no-restricted-imports -- this file defines the abstraction
import type {
    AccessorFn,
    CellContext as CellContextCore,
    CellData,
    Column as ColumnCore,
    ColumnDefBase,
    ColumnDef as ColumnDefCore,
    ColumnPinningState,
    DeepKeys,
    DeepValue,
    DisplayColumnDef as DisplayColumnDefCore,
    ColumnMeta as GlobalColumnMeta,
    Header as HeaderCore,
    OnChangeFn,
    Row as RowCore,
    RowData,
    SortingState,
    StringOrTemplateHeader,
    TableFeatures as TableFeaturesCore,
    TableOptions,
} from '@tanstack/react-table';
// eslint-disable-next-line no-restricted-imports -- this file defines the abstraction
import {
    columnFilteringFeature,
    columnPinningFeature,
    columnSizingFeature,
    columnVisibilityFeature,
    createColumnHelper as createColumnHelperCore,
    createFilteredRowModel,
    createSortedRowModel,
    flexRender,
    globalFilteringFeature,
    metaHelper,
    rowSelectionFeature,
    rowSortingFeature,
    sortFn_alphanumeric,
    sortFn_datetime,
    sortFn_text,
    tableFeatures,
    useTable,
} from '@tanstack/react-table';
import { isUndefined, omit, omitBy } from 'lodash-es';
import type { AriaAttributes, CSSProperties, ComponentPropsWithRef, ReactElement, ReactNode } from 'react';
import { memo, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import {
    Table,
    TableBody,
    TableCell,
    TableCellEmpty,
    TableCellLoading,
    TableHead,
    TableHeader,
    TableRow,
    TableSortHeader,
} from '../primitives/table.tsx';
import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintTitle,
    InfoHintTrigger,
} from './info-hint.tsx';
import type { ResponsiveDataTableProps } from './responsive-data-table.tsx';
import { ResponsiveDataTable } from './responsive-data-table.tsx';

export type SortDirection = 'asc' | 'desc';

export type ColumnSort<TColumnId extends string = string> = {
    id: TColumnId;
    direction: SortDirection;
};

export type ColumnMeta<TSummary = never> = GlobalColumnMeta<TableFeaturesCore, RowData> & {
    align?: 'left' | 'right';
    preserveOpacityWhenDimmed?: boolean;
    enabled?: boolean;
    pinned?: 'left' | 'right';
    summary?: (data: TSummary) => ReactNode;
    hint?: ReactNode;
    onHintOpen?: () => void;
};

export const dataTableFeatures = tableFeatures({
    columnFilteringFeature,
    columnPinningFeature,
    columnSizingFeature,
    columnVisibilityFeature,
    globalFilteringFeature,
    rowSelectionFeature,
    rowSortingFeature,
    columnMeta: metaHelper<ColumnMeta>(),
    filteredRowModel: createFilteredRowModel(),
    sortedRowModel: createSortedRowModel(),
    sortFns: {
        alphanumeric: sortFn_alphanumeric,
        datetime: sortFn_datetime,
        text: sortFn_text,
    },
});

type TableFeatures<TSummary = never, TExtraColumnMeta extends object = object> = Omit<
    typeof dataTableFeatures,
    'columnMeta'
> & {
    columnMeta: ColumnMeta<TSummary> & TExtraColumnMeta;
};

type AccessorKeyColumnId<TColumnId extends string> = Exclude<TColumnId, `${string}.${string}`>;

type AccessorKeyIdentifier<TColumnId extends string> = {
    id?: undefined;
    accessorKey: NoInfer<AccessorKeyColumnId<TColumnId>>;
};

type ColumnIdentifier<TColumnId extends string> =
    | { id: NoInfer<TColumnId> }
    | AccessorKeyIdentifier<TColumnId>;

function isIdentifiedByAccessorKey<TColumnId extends string>(
    column: ColumnIdentifier<TColumnId>,
): column is AccessorKeyIdentifier<TColumnId> {
    return column.id === undefined;
}

export function resolveColumnId<TColumnId extends string>(column: ColumnIdentifier<TColumnId>): TColumnId {
    return isIdentifiedByAccessorKey(column) ? column.accessorKey : column.id;
}

export type ColumnDef<
    TColumnId extends string,
    TData extends RowData,
    TSummary = never,
    TExtraColumnMeta extends object = object,
    TValue extends CellData = CellData,
> = ColumnDefCore<TableFeatures<TSummary, TExtraColumnMeta>, TData, TValue> & {
    id?: TColumnId;
} & ColumnIdentifier<TColumnId>;

export type Column<
    TData extends RowData,
    TSummary = never,
    TExtraColumnMeta extends object = object,
    TValue extends CellData = CellData,
> = ColumnCore<TableFeatures<TSummary, TExtraColumnMeta>, TData, TValue>;

export type Row<TData extends RowData, TSummary = never, TExtraColumnMeta extends object = object> = RowCore<
    TableFeatures<TSummary, TExtraColumnMeta>,
    TData
>;

export type CellContext<
    TData extends RowData,
    TSummary = never,
    TExtraColumnMeta extends object = object,
    TValue extends CellData = CellData,
> = CellContextCore<TableFeatures<TSummary, TExtraColumnMeta>, TData, TValue>;

export type DisplayColumnDef<
    TColumnId extends string,
    TData extends RowData,
    TSummary = never,
    TExtraColumnMeta extends object = object,
    TValue extends CellData = CellData,
> = DisplayColumnDefCore<TableFeatures<TSummary, TExtraColumnMeta>, TData, TValue> & { id: TColumnId };

type HelperColumnDef<
    TData extends RowData,
    TSummary,
    TExtraColumnMeta extends object,
    TValue extends CellData,
> = ColumnDefBase<TableFeatures<TSummary, TExtraColumnMeta>, TData, TValue> & {
    header?: StringOrTemplateHeader<TableFeatures<TSummary, TExtraColumnMeta>, TData, TValue>;
};

type AccessorValue<TData extends RowData, TAccessor> =
    TAccessor extends AccessorFn<TData, infer TReturn>
        ? TReturn
        : TAccessor extends DeepKeys<TData>
          ? DeepValue<TData, TAccessor>
          : never;

export type ColumnHelper<
    TColumnId extends string,
    TData extends RowData,
    TSummary = never,
    TExtraColumnMeta extends object = object,
> = {
    accessor: <TAccessor extends AccessorFn<TData> | DeepKeys<TData>>(
        accessor: TAccessor,
        column: HelperColumnDef<TData, TSummary, TExtraColumnMeta, AccessorValue<TData, TAccessor>> &
            (TAccessor extends AccessorKeyColumnId<TColumnId> ? { id?: TColumnId } : { id: TColumnId }),
    ) => ColumnDef<TColumnId, TData, TSummary, TExtraColumnMeta, AccessorValue<TData, TAccessor>>;
    display: (
        column: HelperColumnDef<TData, TSummary, TExtraColumnMeta, unknown> & {
            id: TColumnId;
        },
    ) => DisplayColumnDef<TColumnId, TData, TSummary, TExtraColumnMeta>;
    columns: (
        columns: readonly ColumnIdentifier<TColumnId>[],
    ) => ColumnDef<TColumnId, TData, TSummary, TExtraColumnMeta>[];
};

export function createColumnHelper<
    TColumnId extends string,
    TData extends RowData,
    TSummary = never,
    TExtraColumnMeta extends object = object,
>(): ColumnHelper<TColumnId, TData, TSummary, TExtraColumnMeta> {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- the core helper returns its input unchanged, so every column keeps the id it was given
    return createColumnHelperCore<
        TableFeatures<TSummary, TExtraColumnMeta>,
        TData
    >() as unknown as ColumnHelper<TColumnId, TData, TSummary, TExtraColumnMeta>;
}

// eslint-disable-next-line no-restricted-imports -- this file defines the abstraction
export type {
    AccessorFn,
    CellData,
    ColumnPinningState,
    ColumnVisibilityState,
    FilterFnOption,
    OnChangeFn,
    RowData,
    RowSelectionState,
    SortingState,
    Updater,
} from '@tanstack/react-table';

type SupportedTableOptions<TData extends RowData, TSummary, TExtraColumnMeta extends object> = Pick<
    TableOptions<TableFeatures<TSummary, TExtraColumnMeta>, TData>,
    | 'defaultColumn'
    | 'enableMultiRowSelection'
    | 'globalFilterFn'
    | 'manualSorting'
    | 'onGlobalFilterChange'
    | 'onRowSelectionChange'
> & {
    state?: Omit<
        NonNullable<TableOptions<TableFeatures<TSummary, TExtraColumnMeta>, TData>['state']>,
        'sorting'
    >;
};

type LegacyDataTableProps<
    TColumnId extends string,
    TData extends RowData,
    TSummary,
    TExtraColumnMeta extends object,
> = SupportedTableOptions<TData, TSummary, TExtraColumnMeta> &
    Omit<ComponentPropsWithRef<'table'>, 'summary'> & {
        columns: readonly ColumnDef<TColumnId, TData, TSummary, TExtraColumnMeta>[];
        data: readonly TData[] | undefined;
        emptyLabel: ReactNode;
        isLoading?: boolean;
        totalRowsCount?: number;
        onRowClick?: (row: Row<TData, TSummary, TExtraColumnMeta>) => void;
        disableHeader?: boolean;
        summary?: TSummary;
        dimNonSelectedRows?: boolean;
        isFetchingNextPage?: boolean;
        hasNextPage?: boolean;
        onFetchNextPage?: () => void;
        sorting?: ColumnSort<TColumnId>;
        onSortingChange?: (sort: ColumnSort<TColumnId> | undefined) => void;
    };

const emptyData: [] = [];

export type DataTableProps<
    TColumnId extends string,
    TData extends RowData,
    TSummary = never,
    TExtraColumnMeta extends object = object,
> =
    | ({ responsive?: false } & LegacyDataTableProps<TColumnId, TData, TSummary, TExtraColumnMeta>)
    | ({ responsive: boolean } & ResponsiveDataTableProps<TColumnId, TData, TSummary, TExtraColumnMeta>);

export function DataTable<
    TColumnId extends string,
    TData extends RowData,
    TSummary = never,
    TExtraColumnMeta extends object = object,
>(props: DataTableProps<TColumnId, TData, TSummary, TExtraColumnMeta>): ReactElement {
    if (props.responsive) {
        return <ResponsiveDataTable {...omit(props, 'responsive')} />;
    }

    return (
        <LegacyDataTable
            {...omit(
                props,
                'responsive',
                'sortSheetTitle',
                'sortAscendingLabel',
                'sortDescendingLabel',
                'sortSheetCloseLabel',
            )}
        />
    );
}

function LegacyDataTable<
    TColumnId extends string,
    TData extends RowData,
    TSummary = never,
    TExtraColumnMeta extends object = object,
>({
    disableHeader = false,
    emptyLabel,
    isLoading = false,
    totalRowsCount = Number.POSITIVE_INFINITY,
    onRowClick,
    data,
    isFetchingNextPage,
    onFetchNextPage,
    hasNextPage,
    summary,
    dimNonSelectedRows = false,
    columns,
    state,
    defaultColumn,
    enableMultiRowSelection,
    globalFilterFn,
    manualSorting,
    onGlobalFilterChange,
    onRowSelectionChange,
    sorting,
    onSortingChange,
    ref,
    ...tableProps
}: LegacyDataTableProps<TColumnId, TData, TSummary, TExtraColumnMeta>): ReactElement {
    const isSortingControlled = onSortingChange !== undefined;

    const enabledColumns = useMemo(
        () =>
            columns
                .filter((column) => column.meta?.enabled !== false)
                .map((column) => ({ ...column, enableSorting: column.enableSorting === true })),
        [columns],
    );

    const sortingState = useMemo<SortingState | undefined>(() => {
        if (sorting === undefined) {
            return isSortingControlled ? [] : undefined;
        }

        const isSortable = enabledColumns.some(
            (column) => resolveColumnId<TColumnId>(column) === sorting.id && column.enableSorting,
        );

        return isSortable ? [{ id: sorting.id, desc: sorting.direction === 'desc' }] : [];
    }, [enabledColumns, isSortingControlled, sorting]);

    const handleSortingChange: OnChangeFn<SortingState> | undefined =
        onSortingChange &&
        ((updater) => {
            const [next] = typeof updater === 'function' ? updater(sortingState ?? []) : updater;
            const columnId = enabledColumns
                .map((column) => resolveColumnId<TColumnId>(column))
                .find((id) => id === next?.id);

            onSortingChange(
                next && columnId !== undefined
                    ? { id: columnId, direction: next.desc ? 'desc' : 'asc' }
                    : undefined,
            );
        });

    const columnPinning = useMemo<ColumnPinningState>(
        () => ({
            start: pinnedColumnIds(enabledColumns, 'left'),
            end: pinnedColumnIds(enabledColumns, 'right'),
        }),
        [enabledColumns],
    );

    const table = useTable<TableFeatures<TSummary, TExtraColumnMeta>, TData>({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- It's safe because columnMeta is just a type helper
        features: dataTableFeatures as TableFeatures<TSummary, TExtraColumnMeta>,
        ...omitBy(
            {
                defaultColumn,
                enableMultiRowSelection,
                globalFilterFn,
                manualSorting: manualSorting ?? (isSortingControlled || undefined),
                onGlobalFilterChange,
                onRowSelectionChange,
                onSortingChange: handleSortingChange,
            },
            isUndefined,
        ),
        columns: enabledColumns,
        state: {
            columnPinning,
            ...omitBy({ sorting: sortingState }, isUndefined),
            ...omitBy(state, isUndefined),
        },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- readonly widening on data prop
        data: (data ?? emptyData) as TData[],
    });

    const leafColumns = (table.getHeaderGroups().at(-1)?.headers ?? []).map((header) => header.column);
    const columnsCount = leafColumns.length;
    const rowCount = table.getRowModel().rows.length || 0;
    const isEmptyTable = rowCount === 0;
    const hasSelectedVisibleRow = table.getSelectedRowModel().rows.length > 0;

    const summaryRow = useMemo(() => {
        if (summary === undefined) {
            return undefined;
        }

        const cells: Record<string, ReactNode> = {};
        for (const column of enabledColumns) {
            if (column.meta?.summary !== undefined) {
                cells[resolveColumnId<TColumnId>(column)] = column.meta.summary(summary);
            }
        }
        return cells;
    }, [enabledColumns, summary]);

    const { ref: lastRowToFetchRef } = useInView({
        threshold: 0.1,
        onChange: (inView) => {
            if (inView && onFetchNextPage && !isFetchingNextPage && hasNextPage) {
                onFetchNextPage();
            }
        },
        skip: isEmptyTable,
    });

    return (
        <Table
            {...tableProps}
            ref={ref}
            style={{ minWidth: isEmptyTable ? undefined : table.getCenterTotalSize() }}
        >
            {!disableHeader && (
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                                return (
                                    <TableHead
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        style={getCommonPinningStyles(header.column)}
                                        align={
                                            header.column.columnDef.meta?.align === 'right' ? 'right' : 'left'
                                        }
                                        aria-sort={getAriaSort(header.column)}
                                    >
                                        {header.isPlaceholder || isLoading || isEmptyTable ? null : (
                                            <ColumnHeader header={header} />
                                        )}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    ))}

                    {summaryRow && (
                        <TableRow>
                            {leafColumns.map((column) => {
                                const value = summaryRow[column.id];
                                const align = column.columnDef.meta?.align === 'right' ? 'right' : 'left';

                                return (
                                    <TableHead
                                        key={column.id}
                                        align={align}
                                        style={getCommonPinningStyles(column)}
                                        className="bg-white font-bold text-neutral-950"
                                    >
                                        {value}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    )}
                </TableHeader>
            )}

            <TableBody>
                {!isLoading && !isEmptyTable
                    ? table.getRowModel().rows.map((row, idx, arr) => {
                          const isDimmed =
                              dimNonSelectedRows && hasSelectedVisibleRow && !row.getIsSelected();

                          return (
                              <TableRow
                                  key={row.id}
                                  ref={
                                      // This can improve UX by fetching in the future
                                      idx === arr.length - (totalRowsCount === arr.length ? 1 : 10)
                                          ? lastRowToFetchRef
                                          : undefined
                                  }
                                  data-state={row.getIsSelected() && 'selected'}
                                  data-dimmed={isDimmed ? 'true' : undefined}
                                  onClick={
                                      onRowClick
                                          ? () => {
                                                onRowClick(row);
                                            }
                                          : undefined
                                  }
                              >
                                  {leafColumns.map((column) => {
                                      const cell = row.getAllCellsByColumnId()[column.id];
                                      if (cell === undefined) {
                                          return null;
                                      }
                                      const meta = cell.column.columnDef.meta;
                                      const dimCell = isDimmed && !meta?.preserveOpacityWhenDimmed;
                                      return (
                                          <TableCell
                                              key={cell.id}
                                              data-state={row.getIsSelected() && 'selected'}
                                              className={
                                                  cell.column.getIsPinned()
                                                      ? 'bg-white group-hover:bg-neutral-25'
                                                      : ''
                                              }
                                              style={getCommonPinningStyles(cell.column, dimCell)}
                                              align={meta?.align === 'right' ? 'right' : 'left'}
                                          >
                                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                          </TableCell>
                                      );
                                  })}
                              </TableRow>
                          );
                      })
                    : null}

                {isLoading || isFetchingNextPage ? (
                    <TableCellLoading colSpan={columnsCount} className={isFetchingNextPage ? 'h-32' : ''} />
                ) : null}

                {!isLoading && isEmptyTable ? (
                    <TableCellEmpty colSpan={columnsCount}>{emptyLabel}</TableCellEmpty>
                ) : null}
            </TableBody>
        </Table>
    );
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- memo doesn't change the component type
export const MemoDataTable = memo(DataTable) as typeof DataTable;

type ColumnHeaderProps<TData extends RowData, TSummary, TExtraColumnMeta extends object> = {
    header: HeaderCore<TableFeatures<TSummary, TExtraColumnMeta>, TData>;
};

function ColumnHeader<TData extends RowData, TSummary, TExtraColumnMeta extends object>({
    header,
}: ColumnHeaderProps<TData, TSummary, TExtraColumnMeta>): ReactNode {
    const { column } = header;
    const label = flexRender(column.columnDef.header, header.getContext());
    const meta = column.columnDef.meta;
    const hint =
        meta?.hint === undefined ? undefined : (
            <InfoHint
                onOpenChange={(open) => {
                    if (open) {
                        meta.onHintOpen?.();
                    }
                }}
            >
                <InfoHintTrigger />
                <InfoHintContent side="bottom">
                    <InfoHintHeader>
                        <InfoHintTitle>{label}</InfoHintTitle>
                    </InfoHintHeader>
                    <InfoHintBody>{meta.hint}</InfoHintBody>
                </InfoHintContent>
            </InfoHint>
        );

    if (!column.getCanSort()) {
        return hint === undefined ? (
            label
        ) : (
            <span className="inline-flex items-center gap-tight align-middle">
                {label}
                {hint}
            </span>
        );
    }

    const sorted = column.getIsSorted();

    return (
        <TableSortHeader
            title={label}
            align={meta?.align}
            direction={sorted === false ? undefined : sorted}
            onDirectionChange={(direction) => {
                column.toggleSorting(direction === 'desc');
            }}
        >
            {hint}
        </TableSortHeader>
    );
}

function getAriaSort<TData extends RowData, TSummary, TExtraColumnMeta extends object>(
    column: Column<TData, TSummary, TExtraColumnMeta>,
): AriaAttributes['aria-sort'] {
    if (!column.getCanSort()) {
        return undefined;
    }
    switch (column.getIsSorted()) {
        case 'asc':
            return 'ascending';
        case 'desc':
            return 'descending';
        case false:
            return 'none';
    }
}

export function getColumnMeta<
    TColumnId extends string,
    TData extends RowData,
    TSummary,
    TExtraColumnMeta extends object,
>(
    columns: readonly ColumnDef<TColumnId, TData, TSummary, TExtraColumnMeta>[],
    id: NoInfer<TColumnId>,
): (ColumnMeta<TSummary> & TExtraColumnMeta) | undefined {
    return columns.find(
        (column) => column.meta?.enabled !== false && resolveColumnId<TColumnId>(column) === id,
    )?.meta;
}

function pinnedColumnIds<
    TColumnId extends string,
    TData extends RowData,
    TSummary,
    TExtraColumnMeta extends object,
>(columns: ColumnDef<TColumnId, TData, TSummary, TExtraColumnMeta>[], side: 'left' | 'right'): TColumnId[] {
    return columns
        .filter((column) => column.meta?.pinned === side)
        .map((column) => resolveColumnId<TColumnId>(column));
}

function getCommonPinningStyles<TData extends RowData, TSummary, TExtraColumnMeta extends object>(
    column: Column<TData, TSummary, TExtraColumnMeta>,
    isDimmed = false,
): CSSProperties {
    const isPinned = column.getIsPinned();
    let opacity = 1;
    if (isPinned) {
        opacity = 0.95;
    }
    if (isDimmed) {
        opacity = 0.5;
    }
    return {
        left: isPinned === 'start' ? `${column.getStart('start')}px` : undefined,
        right: isPinned === 'end' ? `${column.getAfter('end')}px` : undefined,
        opacity,
        position: isPinned ? 'sticky' : 'relative',
        width: column.getSize(),
        zIndex: isPinned ? 1 : 0,
    };
}
